import { is, List } from "immutable";
import * as React from "react";
import * as BT from "react-bootstrap-table";
import { connect } from "react-redux";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import * as redux from "redux";
import * as Actions from "../../../actions";
import * as Comp from "../../../components";
import { Paths } from "../../../constants";
import * as M from "../../../models";
import { AppState } from "../../../store";
import { Utility } from "../../../utils";

type DataRow = M.Abstract.AbstractGraderListItem & {
    gradingStatus: string;
};

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => ({
    ...Comp.Base.DefaultListViewStatePropsMap(state, ownProps),
    abstracts: state.abstractGrading.listAbstracts,
    reviewer: state.abstractGrading.reviewerStats,
    loadingAbstracts: state.abstractGrading.loadingListAbstracts,
});

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
    ...Comp.Base.DefaultDispatchPropsMap(dispatch),
    loadAbstracts: () => dispatch(Actions.AbstractGrading.loadListAbstracts()),
    toggleAbstractsLoading: (loading: boolean) => dispatch(Actions.AbstractGrading.toggleLoadingListAbstracts(loading)),
    saveReviewerStats: (r: M.Abstract.ReviewerStats) => dispatch(Actions.AbstractGrading.onReviewerStatsUpdated(r)),
});

type OwnProps = RouteComponentProps<{}> & {
    eventId: number;
};

type ConnectedState = Comp.Base.DefaultListViewConnectedState & {
    abstracts: List<M.Abstract.AbstractGraderListItem>;
    reviewer?: M.Abstract.ReviewerStats;
    loadingAbstracts: boolean;
};

type ConnectedDispatch = Comp.Base.DefaultConnectedDispatch & {
    loadAbstracts: () => void;
    toggleAbstractsLoading: (loading: boolean) => void;
    saveReviewerStats: (r: M.Abstract.ReviewerStats) => void;
};

interface OwnState {
    abstracts: List<DataRow>;
}

type CombinedProps = ConnectedState & ConnectedDispatch & OwnProps;

class AbstractsComponent extends Comp.Base.BaseListViewComponent<CombinedProps, OwnState> {

    public constructor(props: any) {
        super(props);
        this.state = {
            ...this.getInitialListViewState(props),
            abstracts: List(),
            loading: true,
        };
    }

    public async componentDidMount() {
        this._isMounted = true;

        // check user's status as a reviewer
        const hasAccess = await M.Abstract.checkReviewerAccess(this.props);
        if (!hasAccess) {
            this.props.notifyError("You do not have access to view the requested page.");
            this.props.goto(Paths.User.Account);
            return;
        }

        await this.setStateAsync({ ...this.state, loading: false });
        await this.initializeComponent();
    }

    public componentDidUpdate(prevProps: Readonly<CombinedProps>) {
        if (!is(prevProps.abstracts, this.props.abstracts))
            this.loadAbstractsFromState();

        // just in case a different guid in URL has been detected
        if (prevProps.eventId !== this.props.eventId) {
            this.clearState(() => {
                this.initializeComponent();
            });
        }
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public render() {
        const eventId = this.props.eventId;
        const loading = this.state.loading || !!this.props.loadingAbstracts[eventId];

        const tableActions = this.getTableActions({
            dataLength: this.state.abstracts.size,
            filteredLength: this.state.abstracts.size,
            searchPlaceholder: "Search abstracts...",
        });

        const noAbstractsMsg = !this.props.loadingAbstracts && !this.props.abstracts.size
            ? <p>There are currently no abstracts to grade.</p>
            : null;

        const table = this.props.loadingAbstracts || (!this.props.loadingAbstracts && this.props.abstracts.size)
            ? <>
                {tableActions}

                <BT.BootstrapTable
                    bordered={false}
                    data={this.state.abstracts.toArray()}
                    pagination={this.state.tablePageSize > -1}
                    options={{
                        afterSearch: (searchText, results) => this.onTableSearch(searchText, results),
                        page: this.state.tablePage,
                        paginationPanel: () => <div />,
                        sizePerPage: this.state.tablePageSize,
                        onSortChange: (sortName: any, sortOrder: any) => this.handleTableSort(sortOrder, sortName),
                        defaultSortName: "id",
                        defaultSortOrder: "asc",
                    }}
                    ref={(ref: any) => this.table = ref}
                >
                    <BT.TableHeaderColumn caretRender={Comp.Table.BootstrapTableHelpers.renderCaret} dataField="id" dataSort isKey width="70px">#</BT.TableHeaderColumn>

                    <BT.TableHeaderColumn
                        caretRender={Comp.Table.BootstrapTableHelpers.renderCaret}
                        dataField="title"
                        dataFormat={(cell: any, row: DataRow) => (
                            <Link
                                dangerouslySetInnerHTML={{ __html: row.title }}
                                title={Utility.stripHtml(row.title) || ""}
                                to={Paths.UserAbstractGradingDetails(row.guid)}
                            />
                        )}
                        dataSort
                    >Title</BT.TableHeaderColumn>

                    <BT.TableHeaderColumn caretRender={Comp.Table.BootstrapTableHelpers.renderCaret} dataField="typeShortName" dataSort width="11%">Format</BT.TableHeaderColumn>
                    <BT.TableHeaderColumn caretRender={Comp.Table.BootstrapTableHelpers.renderCaret} dataField="classification" dataSort width="25%">Classification</BT.TableHeaderColumn>
                    <BT.TableHeaderColumn caretRender={Comp.Table.BootstrapTableHelpers.renderCaret} dataField="gradingStatus" dataSort width="11%">Status</BT.TableHeaderColumn>
                </BT.BootstrapTable>
            </>
            : null;

        return <Comp.Page.PublicPage additionalClasses="full" pageTitle="Your Abstracts" style={{ minHeight: "200px", position: "relative" }}>

            {this.getErrorAlert()}

            <Comp.Generic.LoadingSpinner show={loading} />

            <h1>Grade Abstracts</h1>

            {noAbstractsMsg}

            {table}

        </Comp.Page.PublicPage>;
    }

    private clearState(callback?: () => void) {
        this.setState({
            abstracts: List(),
        }, callback);
    }

    private async initializeComponent() {
        await this.loadAbstractsFromState(true);
        this.props.loadAbstracts();
    }

    private async loadAbstractsFromState(toggleLoading: boolean = false) {
        const abstracts: List<DataRow> = this.props.abstracts
            .map((x: M.Abstract.AbstractGraderListItem) => ({
                ...x,
                gradingStatus: M.Abstract.GradingStatuses.isGraded(x) ? "Complete" : "Incomplete",
            }))
            .toList();

        await this.setStateAsync({ ...this.state, abstracts });

        if (!abstracts.size && toggleLoading)
            this.props.toggleAbstractsLoading(true);

        // update table state from location state if available
        this.updateTableState(abstracts);
    }
}

export const Abstracts: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(withRouter(AbstractsComponent) as any);



// WEBPACK FOOTER //
// ./src/containers/user/abstractGrading/Abstracts.tsx